// chat/voice.tsx — FREE, on-device voice for the App Brain chat (Oriora fleet standard).
// ONE button, INSIDE the existing chat: 🎤 dictation into the SAME composer. Tap it → speak → your
// words fill the chat box LIVE → tap again (or stop talking) → review → press Send. NO auto-send:
// the human presses Send, so it never fires on a half-sentence or a misheard phrase. No separate
// voice UI, no second chat. Self-contained (controlled by value/onChange) so it drops into any app's
// composer with a single line. Runs entirely on the device (Web Speech API) — nothing audio leaves it
// (zero egress). Self-hides where the API is absent (older iOS webview), where the keyboard mic dictates.
import { useEffect, useRef, useState } from "react";

// ── speak-out: device text-to-speech (🔊 read an assistant reply aloud) ─────────
const synth: SpeechSynthesis | undefined =
  typeof window !== "undefined" ? window.speechSynthesis : undefined;

// Best free device voice (review finding: the engine default is the compact robotic one).
// Prefer Premium/Enhanced voices, then the known-good named ones, then any English. Free, on-device.
let bestVoice: SpeechSynthesisVoice | null | undefined;
function pickBestVoice(): SpeechSynthesisVoice | null {
  if (bestVoice !== undefined) return bestVoice;
  const en = (synth?.getVoices() ?? []).filter((v) => v.lang?.toLowerCase().startsWith("en"));
  bestVoice =
    en.find((v) => /premium/i.test(v.name)) ??
    en.find((v) => /enhanced/i.test(v.name)) ??
    ["Ava", "Samantha", "Serena", "Allison", "Zoe", "Kate"].map((n) => en.find((v) => v.name?.startsWith(n))).find(Boolean) ??
    en[0] ?? null;
  return bestVoice ?? null;
}
if (synth && "onvoiceschanged" in synth) {
  synth.addEventListener?.("voiceschanged", () => { bestVoice = undefined; });
}

const SPK: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
  border: "1px solid var(--border, #d9d4ea)", background: "transparent",
  color: "var(--ink-2, #6b6580)", borderRadius: 999, padding: "3px 10px",
  fontSize: 12.5, fontWeight: 600, lineHeight: 1.4,
};

export function SpeakButton({ text }: { text: string }) {
  const [on, setOn] = useState(false);
  useEffect(() => () => { try { synth?.cancel(); } catch { /* ignore */ } }, []);
  if (!synth || !text.trim()) return null;
  const toggle = () => {
    try {
      if (on) { synth.cancel(); setOn(false); return; }
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = pickBestVoice();
      if (v) u.voice = v;
      u.onend = () => setOn(false);
      u.onerror = () => setOn(false);
      setOn(true);
      synth.speak(u);
    } catch { setOn(false); }
  };
  return (
    <button type="button"
      style={on ? { ...SPK, color: "var(--accent, #6c5ce7)", borderColor: "var(--accent, #6c5ce7)" } : SPK}
      aria-label={on ? "Stop reading" : "Read aloud"} title={on ? "Stop" : "Read aloud"}
      onClick={toggle}>{on ? "■ Stop" : "🔊 Listen"}</button>
  );
}

function getSR(): (new () => any) | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
}

const MIC: React.CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 999, padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--border, #d9d4ea)", background: "var(--elev-2, #ffffff)",
  color: "var(--ink-1, #1f1b2e)", fontSize: 18, lineHeight: 1,
  transition: "background .15s, color .15s, box-shadow .15s, border-color .15s",
};
const MIC_ON: React.CSSProperties = {
  background: "var(--accent, #6c5ce7)", borderColor: "var(--accent, #6c5ce7)",
  color: "#ffffff", boxShadow: "0 0 0 4px rgba(108,92,231,.20)",
};

// Controlled: reads the current composer text via `value`, writes the growing transcript via `onChange`.
// It snapshots whatever was already typed when you tap 🎤, then appends your live dictation to it.
export function MicButton(
  { value, onChange, disabled }: { value: string; onChange: (t: string) => void; disabled?: boolean },
) {
  const [listening, setListening] = useState(false);
  // 🔴 G28 COROLLARY (2026-08-26): a swallowed permission denial IS the dead button.
  // The old handler only reset the button state and said
  // NOTHING, so a denied mic looked identical to "nothing happened". That is verbatim the
  // rejection we took: Guideline 2.1(a), "the microphone icon was unresponsive" (Content +
  // Dev, macOS, 2026-07-23). On the sandboxed Mac App Store build a denial is the NORMAL
  // outcome when the entitlement is absent, so silence there is guaranteed, not unlucky.
  const [micErr, setMicErr] = useState<string | null>(null);
  const recRef = useRef<any>(null);
  const baseRef = useRef("");
  // Mirror the latest composer text + track what the mic last wrote, so onresult can
  // tell when Send/typing changed the field out from under it (see the guard below).
  const valueRef = useRef(value); valueRef.current = value;
  const lastEmitRef = useRef("");
  const consumedRef = useRef(0);
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  const SR = getSR();
  if (!SR) return null; // older iOS webview: system keyboard mic already dictates → no dead button
  const toggle = () => {
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    setMicErr(null);
    try {
      const rec = new SR();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = true;   // live words as you speak
      rec.continuous = true;       // keep listening across pauses until you tap stop
      baseRef.current = (value || "").trim();
      lastEmitRef.current = value || "";
      consumedRef.current = 0;
      rec.onresult = (e: any) => {
        // The composer can change from OUTSIDE the mic — you press Send (it clears the box) or
        // you edit the text. `continuous` keeps the whole transcript in e.results, so without
        // this guard a late result after Send would REFILL the emptied box with everything you
        // just sent. If what's there no longer matches what the mic last wrote, re-baseline onto
        // it and treat everything spoken so far as consumed; if Send emptied it, stop dictating.
        if (valueRef.current !== lastEmitRef.current) {
          if (!valueRef.current.trim()) { try { rec.stop(); } catch { /* ignore */ } setListening(false); return; }
          baseRef.current = valueRef.current.trim();
          consumedRef.current = e.results.length;
        }
        let txt = "";
        for (let i = consumedRef.current; i < e.results.length; i++) txt += e.results[i][0]?.transcript ?? "";
        txt = txt.replace(/\s+/g, " ").trim();
        const out = (baseRef.current ? baseRef.current + " " : "") + txt;
        lastEmitRef.current = out;
        onChange(out);
      };
      rec.onend = () => setListening(false);
      rec.onerror = (ev: any) => {
        setListening(false);
        // Say WHY. "no-speech"/"aborted" are ordinary (you stopped, or said nothing) — those
        // stay quiet; everything else gets a visible, actionable line.
        const code = ev?.error;
        if (code === "not-allowed" || code === "service-not-allowed")
          setMicErr("Microphone access is off for this app. Turn it on in Settings › Privacy & Security › Microphone, then try again.");
        else if (code === "audio-capture") setMicErr("No microphone was found on this device.");
        else if (code === "network") setMicErr("Dictation needs a network connection.");
        else if (code && code !== "no-speech" && code !== "aborted") setMicErr("Dictation stopped unexpectedly.");
      };
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); setMicErr("Dictation could not start on this device."); }
  };
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <button type="button"
        style={listening ? { ...MIC, ...MIC_ON } : MIC}
        aria-label={listening ? "Stop dictation" : "Speak"} title={listening ? "Listening… tap to stop" : "Speak"}
        onClick={toggle} disabled={disabled}>{listening ? "■" : "🎤"}</button>
      {micErr && (
        <span role="alert" onClick={() => setMicErr(null)} style={{
          position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 50, width: 232,
          background: "var(--elev-2, #ffffff)", color: "var(--ink-1, #1f1b2e)", cursor: "pointer",
          border: "1px solid var(--border, #d9d4ea)", borderRadius: 10, padding: "8px 10px",
          fontSize: 12, lineHeight: 1.35, textAlign: "left", boxShadow: "0 6px 20px rgba(0,0,0,.16)",
        }}>{micErr}</span>
      )}
    </span>
  );
}
